import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { DATEPICKER_DATE_FORMAT, MOMENT_DATE_FORMAT } from '@app/app.constants';
import { Logger } from '@core/services/logger.service';
import { momentToNgbDate } from '@core/utils/core.util';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/internal/operators/tap';
import { DateTimeType } from '../../shared.types';

@Component({
  selector: 'app-date-time',
  styleUrls: ['date-time.component.scss'],
  templateUrl: 'date-time.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    TranslateModule,
  ],
})
export class DateTimeComponent {
  private formBuilder = inject(UntypedFormBuilder);
  private destroyRef = inject(DestroyRef);

  readonly DATEPICKER_DATE_FORMAT = DATEPICKER_DATE_FORMAT;
  readonly MOMENT_DATE_FORMAT = MOMENT_DATE_FORMAT;

  DateTimeType = DateTimeType;
  dateTimeType: DateTimeType;

  min = moment().add(-100, 'year');
  max = moment().add(100, 'year');

  dateTimeForm: any = null;

  protected readonly momentToNgbDate = momentToNgbDate;

  private readonly log = new Logger('DateTimeComponent');

  private dateTimeFormControl: UntypedFormControl = null;
  private subscriptions: Subscription[] = [];

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('options')
  set _options(options: {
    formControl: UntypedFormControl;
    dateTimeType?: DateTimeType;
    min?: Moment;
    max?: Moment;
  }) {
    const dateTimeType = options.dateTimeType || DateTimeType.DateTime;

    if (options) {
      this.dateTimeFormControl = options.formControl;
      this.dateTimeType = dateTimeType;

      if (options.min) {
        this.min = moment(options.min);
      }
      if (options.max) {
        this.max = moment(options.max);
      }

      const validator = this.dateTimeFormControl.validator;
      this.createForm(validator && (validator as any).required);
    }
  }

  c(controlName: string) {
    return this.dateTimeForm.controls[controlName];
  }

  private createForm(isRequired: boolean) {
    const timeHourValidators = [Validators.pattern('([01]?[0-9]|2[0-3])')];
    const timeMinutesValidators = [Validators.pattern('([0-9]|[0-5][0-9])')];

    let date: Moment | string = '';
    let hour = '';
    let minutes = '';
    if (this.dateTimeFormControl.value) {
      date = moment(this.dateTimeFormControl.value);
      hour = date.format('HH');
      minutes = date.format('mm');
    }

    this.dateTimeForm = this.formBuilder.group(
      Object.assign(
        {
          date: [date, isRequired ? [Validators.required] : []],
        },
        this.dateTimeType === DateTimeType.DateTime
          ? {
              timeHour: [
                hour,
                isRequired
                  ? [Validators.required, ...timeHourValidators]
                  : timeHourValidators,
              ],
              timeMinutes: [
                minutes,
                isRequired
                  ? [Validators.required, ...timeMinutesValidators]
                  : timeMinutesValidators,
              ],
            }
          : {}
      )
    );

    this.updateDisabledState();

    this.dateTimeFormControl.registerOnDisabledChange(() => {
      this.updateDisabledState();
    });

    this.dateTimeFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const date = moment(this.dateTimeFormControl.value);
        this.dateTimeForm.get('date').setValue(date, { emitEvent: false });
      });

    for (const subscription of this.subscriptions) {
      if (subscription) {
        subscription.unsubscribe();
      }
    }
    this.subscriptions = [];

    const handleDateTimeChange = (
      dateValue?: Moment,
      hourValue?: string,
      minutesValue?: string
    ) => {
      if (dateValue !== null) {
        if (dateValue && !moment.isMoment(dateValue)) {
          dateValue = moment(dateValue);
        }
        date = dateValue
          ? dateValue.clone()
          : moment(
              this.dateTimeFormControl.value
                ? this.dateTimeFormControl.value
                : undefined
            );

        if (this.dateTimeType === DateTimeType.DateTime) {
          hourValue = hourValue ?? this.dateTimeForm.controls.timeHour.value;
          minutesValue =
            minutesValue ?? this.dateTimeForm.controls.timeMinutes.value;
        }

        this.log.debug('handle date time change', date.format());
        if (hourValue) {
          try {
            const hourInt = parseInt(hourValue, 10);
            if (hourInt >= 0 && hourInt <= 23) {
              date.set('hour', hourInt);
            }
          } catch (ex) {
            this.dateTimeFormControl.setValue(undefined);
            this.log.error(ex);
          }
        }
        if (minutesValue) {
          try {
            const minutesInt = parseInt(minutesValue, 10);
            if (minutesInt >= 0 && minutesInt <= 59) {
              date.set('minutes', minutesInt);
            }
          } catch (ex) {
            this.dateTimeFormControl.setValue(undefined);
            this.log.error(ex);
          }
        }
        this.dateTimeFormControl.setValue(date);
      } else {
        this.dateTimeFormControl.setValue(undefined);
      }
      this.dateTimeFormControl.markAsTouched();
    };

    this.subscriptions.push(
      this.dateTimeForm.controls.date.valueChanges
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((dateValue: Moment) => handleDateTimeChange(dateValue))
        )
        .subscribe()
    );

    if (this.dateTimeType === DateTimeType.DateTime) {
      this.subscriptions.push(
        this.dateTimeForm.controls.timeHour.valueChanges
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((hourValue: string) =>
              handleDateTimeChange(
                this.dateTimeForm.controls.date.value,
                hourValue
              )
            )
          )
          .subscribe()
      );

      this.subscriptions.push(
        this.dateTimeForm.controls.timeMinutes.valueChanges
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap((minutesValue: string) =>
              handleDateTimeChange(
                this.dateTimeForm.controls.date.value,
                this.dateTimeForm.controls.timeHour.value,
                minutesValue
              )
            )
          )
          .subscribe()
      );
    }
  }

  private updateDisabledState() {
    if (this.dateTimeFormControl.disabled) {
      this.dateTimeForm.disable();
    } else {
      this.dateTimeForm.enable();
    }
  }
}
